import { Box, InputLabel, makeStyles } from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  NumberRangeWithYearPeriod,
  NumberRangeWithYearsFilter,
} from '../../../generated/model/models';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import {
  InputDateRangeComponentWithDropdown,
  YEAR_DROPDOWN_OPTIONS,
} from '../../Inputs/DateRangeWithDropdown';
import InputNumberRangeSlider from '../../Inputs/NumberRangePicker';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import SwitchCompaniesUnknowns from '../../Inputs/SwitchCompaniesUnknowns';
import { allowedYearRange } from '../../Inputs/YearRange/YearRange';

export const defaultPercentageRange = {
  from: -100,
  to: 100,
};

interface Props {
  root?: boolean;
  withUnknownSwitch?: boolean;
  unknownSwitchEnabled?: boolean;
  label?: string;
  onChange: (values: NumberRangeWithYearsFilter) => void;
  defaultAllowedYearRange?: typeof allowedYearRange;
  defaultValue?: NumberRangeWithYearsFilter;
  isPercentage?: boolean;
  active: boolean;
  additionalReadableStatement: any[];
}

const NumberRangePickerWithYearPeriodFilterInput: React.FC<Props> = React.memo(
  ({
    root,
    withUnknownSwitch = false,
    unknownSwitchEnabled,
    additionalReadableStatement = [],
    label,
    onChange,
    defaultAllowedYearRange = allowedYearRange,
    isPercentage = false,
    active,
    defaultValue = withUnknownSwitch
      ? {
          includeEmpty: unknownSwitchEnabled === undefined ? true : unknownSwitchEnabled,
          value: {
            criteria: YEAR_DROPDOWN_OPTIONS[1].value as NumberRangeWithYearPeriod.CriteriaEnum,
            years: defaultAllowedYearRange,
            values: {
              from: undefined,
              fromInclusive: false,
              to: undefined,
              toInclusive: false,
            },
          },
        }
      : {
          value: {
            criteria: YEAR_DROPDOWN_OPTIONS[1].value as NumberRangeWithYearPeriod.CriteriaEnum,
            years: defaultAllowedYearRange,
            values: {
              from: undefined,
              fromInclusive: false,
              to: undefined,
              toInclusive: false,
            },
          },
        },
  }) => {
    const classes = useStyles();
    const [state, setState] = useState<NumberRangeWithYearsFilter>(defaultValue);
    const { t } = useTranslation();

    function renderCriteriaOption(label?: string) {
      return label ? t(label) : '';
    }

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    const rs = [...additionalReadableStatement];
    if (withUnknownSwitch && state.includeEmpty) {
      rs.push({ label: 'Included:', value: 'Companies with unknown data' });
    }
    if (state.value) {
      if (
        state.value.values?.from !== undefined &&
        state.value.values?.from === state.value.values?.to
      ) {
        rs.push({ label: 'Value:', value: `Is equal to ${state.value.values?.from}` });
      } else {
        const from =
          state.value.values?.from == null
            ? '-∞'
            : (state.value.values?.fromInclusive ? '>= ' : '> ') + state.value.values?.from;
        const to =
          state.value.values?.to == null
            ? '∞'
            : (state.value.values?.toInclusive ? '<= ' : '< ') + state.value.values?.to;
        rs.push({
          label: 'Value:',
          value: `From ${from} to ${to}`,
        });
      }
    }
    if (state.value && state.value.criteria) {
      rs.push({
        label: 'Applies for:',
        value: renderCriteriaOption(
          YEAR_DROPDOWN_OPTIONS.find((o) => o.value === state.value!.criteria)?.label,
        ),
      });
    }
    if (state.value && state.value.years?.from && state.value.years?.to) {
      rs.push({
        label: 'Range:',
        value: `From ${state.value.years.from} to ${state.value.years.to}`,
      });
    }

    return (
      <>
        {active ? (
          <>
            {withUnknownSwitch && (
              <SwitchCompaniesUnknowns
                onChange={(value) => {
                  setState({ ...state, includeEmpty: value });
                }}
                checked={state.includeEmpty}
              />
            )}

            <>
              <InputLabel>{label}</InputLabel>
              <InputNumberRangeSlider
                defaultValue={state.value?.values}
                onChange={(numberRange) => {
                  setState({
                    ...state,
                    value: {
                      values: numberRange,
                      criteria: state.value?.criteria,
                      years: state.value?.years,
                    },
                  });
                }}
                isPercentage={isPercentage}
              />
              <Box className={classes.box}>
                <InputDateRangeComponentWithDropdown
                  active={active}
                  allowedYearRange={defaultAllowedYearRange}
                  defaultState={state.value as NumberRangeWithYearPeriod}
                  defaultOptions={YEAR_DROPDOWN_OPTIONS}
                  defaultValue={[state.value?.criteria as NumberRangeWithYearPeriod.CriteriaEnum]}
                  onChange={(dateRangeValues) => {
                    setState({
                      ...state,
                      value: {
                        values: state.value?.values,
                        criteria: dateRangeValues.criteria,
                        years: dateRangeValues.years,
                      },
                    });
                  }}
                />
              </Box>
            </>
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

const useStyles = makeStyles(() => ({
  box: {
    marginTop: '15px',
  },
}));

export default NumberRangePickerWithYearPeriodFilterInput;
